import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { 
  CreditCard, 
  DollarSign, 
  Lock, 
  Check, 
  Crown,
  Loader2
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";

// Payment method icons/logos
const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#00457C">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
  </svg>
);

const CashAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#00D632">
    <path d="M23.59 3.47A5.1 5.1 0 0 0 20.54.42C19.2.05 16.38 0 12 0S4.8.05 3.46.42A5.1 5.1 0 0 0 .41 3.47C.05 4.8 0 7.62 0 12s.05 7.2.41 8.53a5.1 5.1 0 0 0 3.05 3.05c1.34.37 4.16.42 8.54.42s7.2-.05 8.54-.42a5.1 5.1 0 0 0 3.05-3.05c.37-1.33.42-4.15.42-8.53s-.05-7.2-.42-8.53zM17.4 12.83l-.9.9a.76.76 0 0 1-1.06 0l-2.37-2.37-2.37 2.37a.76.76 0 0 1-1.06 0l-.9-.9a.75.75 0 0 1 0-1.06l2.37-2.37-2.37-2.37a.75.75 0 0 1 0-1.06l.9-.9a.76.76 0 0 1 1.06 0l2.37 2.37 2.37-2.37a.76.76 0 0 1 1.06 0l.9.9a.75.75 0 0 1 0 1.06l-2.37 2.37 2.37 2.37a.75.75 0 0 1 0 1.06z"/>
  </svg>
);

const ZelleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#6D1ED4">
    <path d="M13.559 24h-2.841a.483.483 0 0 1-.483-.483V16.2h-8.27a.483.483 0 0 1-.483-.483v-2.842a.483.483 0 0 1 .483-.483h8.27V8.317L2.524 8.31A.483.483 0 0 1 2.04 7.83V4.987a.483.483 0 0 1 .483-.483h8.512V.483A.483.483 0 0 1 11.518 0h2.841a.483.483 0 0 1 .483.483v4.02h7.193a.483.483 0 0 1 .483.484v2.842a.483.483 0 0 1-.483.483h-7.193v4.08l7.717.008a.483.483 0 0 1 .483.483v2.842a.483.483 0 0 1-.483.483h-8.518v7.317a.483.483 0 0 1-.483.483z"/>
  </svg>
);

const PaymentMethodsCard = ({ onUpgradeClick }) => {
  const { token } = useAuth();
  const [methods, setMethods] = useState({
    stripe_enabled: true,
    paypal_enabled: false,
    paypal_email: "",
    cashapp_enabled: false,
    cashapp_tag: "",
    zelle_enabled: false,
    zelle_email: "",
    zelle_phone: "",
    cash_enabled: true,
    preferred_method: "stripe"
  });
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPaymentMethods();
  }, [token]);

  const fetchPaymentMethods = async () => {
    if (!token) return;
    
    try {
      const res = await axios.get(`${API}/payments/methods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHasAccess(res.data.enabled);
      if (res.data.methods) {
        setMethods(prev => ({
          ...prev,
          ...res.data.methods,
          // Auto-enable payment methods if they have data
          paypal_enabled: !!res.data.methods.paypal_email,
          cashapp_enabled: !!res.data.methods.cashapp_tag,
          zelle_enabled: !!(res.data.methods.zelle_email || res.data.methods.zelle_phone)
        }));
      }
    } catch (error) {
      console.error("Failed to fetch payment methods:", error);
    } finally {
      setLoading(false);
    }
  };

  const savePaymentMethods = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/payments/methods`,
        {
          stripe_enabled: methods.stripe_enabled,
          paypal_email: methods.paypal_enabled ? methods.paypal_email : null,
          cashapp_tag: methods.cashapp_enabled ? methods.cashapp_tag : null,
          zelle_email: methods.zelle_enabled ? methods.zelle_email : null,
          zelle_phone: methods.zelle_enabled ? methods.zelle_phone : null,
          cash_enabled: methods.cash_enabled,
          preferred_method: methods.preferred_method
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment methods updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update payment methods");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-stone-800/50 border-stone-700">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500"></div>
        </CardContent>
      </Card>
    );
  }

  if (!hasAccess) {
    return (
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader>
          <CardTitle className="font-display text-xl text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-amber-500" />
            Payment Methods
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Pro</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-500" />
            </div>
            <h3 className="text-white font-semibold mb-2">Accept More Payment Methods</h3>
            <p className="text-gray-400 text-sm mb-4">
              Upgrade to Pro to accept PayPal, Cash App, and Zelle in addition to card payments.
            </p>
            <Button 
              onClick={onUpgradeClick}
              className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-semibold"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Pro - $29/mo
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-stone-800/50 border-stone-700">
      <CardHeader>
        <CardTitle className="font-display text-xl text-white flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-amber-500" />
          Payment Methods
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stripe (Always available) */}
        <div className="p-4 bg-stone-700/50 rounded-lg border border-stone-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#635BFF]/20 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-[#635BFF]" />
              </div>
              <div>
                <p className="text-white font-medium">Credit/Debit Card</p>
                <p className="text-xs text-stone-400">Powered by Stripe</p>
              </div>
            </div>
            <Switch
              checked={methods.stripe_enabled}
              onCheckedChange={(checked) => setMethods({ ...methods, stripe_enabled: checked })}
            />
          </div>
        </div>

        {/* PayPal */}
        <div className="p-4 bg-stone-700/50 rounded-lg border border-stone-600 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#00457C]/20 rounded-lg flex items-center justify-center">
                <PayPalIcon />
              </div>
              <div>
                <p className="text-white font-medium">PayPal</p>
                <p className="text-xs text-stone-400">Enter your PayPal.me username or email</p>
              </div>
            </div>
            <Switch
              checked={methods.paypal_enabled}
              onCheckedChange={(checked) => setMethods({ ...methods, paypal_enabled: checked })}
            />
          </div>
          {methods.paypal_enabled && (
            <Input
              value={methods.paypal_email || ""}
              onChange={(e) => setMethods({ ...methods, paypal_email: e.target.value })}
              placeholder="yourname@email.com or paypal.me/yourname"
              className="bg-stone-600 border-stone-500 text-white"
            />
          )}
        </div>

        {/* Cash App */}
        <div className="p-4 bg-stone-700/50 rounded-lg border border-stone-600 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#00D632]/20 rounded-lg flex items-center justify-center">
                <CashAppIcon />
              </div>
              <div>
                <p className="text-white font-medium">Cash App</p>
                <p className="text-xs text-stone-400">Enter your $cashtag</p>
              </div>
            </div>
            <Switch
              checked={methods.cashapp_enabled}
              onCheckedChange={(checked) => setMethods({ ...methods, cashapp_enabled: checked })}
            />
          </div>
          {methods.cashapp_enabled && (
            <Input
              value={methods.cashapp_tag || ""}
              onChange={(e) => setMethods({ ...methods, cashapp_tag: e.target.value })}
              placeholder="$YourCashTag"
              className="bg-stone-600 border-stone-500 text-white"
            />
          )}
        </div>

        {/* Zelle */}
        <div className="p-4 bg-stone-700/50 rounded-lg border border-stone-600 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#6D1ED4]/20 rounded-lg flex items-center justify-center">
                <ZelleIcon />
              </div>
              <div>
                <p className="text-white font-medium">Zelle</p>
                <p className="text-xs text-stone-400">Enter your Zelle email or phone</p>
              </div>
            </div>
            <Switch
              checked={methods.zelle_enabled}
              onCheckedChange={(checked) => setMethods({ ...methods, zelle_enabled: checked })}
            />
          </div>
          {methods.zelle_enabled && (
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={methods.zelle_email || ""}
                onChange={(e) => setMethods({ ...methods, zelle_email: e.target.value })}
                placeholder="Email"
                className="bg-stone-600 border-stone-500 text-white"
              />
              <Input
                value={methods.zelle_phone || ""}
                onChange={(e) => setMethods({ ...methods, zelle_phone: e.target.value })}
                placeholder="Phone"
                className="bg-stone-600 border-stone-500 text-white"
              />
            </div>
          )}
        </div>

        {/* Cash at Salon */}
        <div className="p-4 bg-stone-700/50 rounded-lg border border-stone-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium">Cash</p>
                <p className="text-xs text-stone-400">Accept cash payments at salon</p>
              </div>
            </div>
            <Switch
              checked={methods.cash_enabled}
              onCheckedChange={(checked) => setMethods({ ...methods, cash_enabled: checked })}
            />
          </div>
        </div>

        {/* Preferred Method */}
        <div>
          <Label className="text-stone-300 mb-2 block">Preferred Payment Method</Label>
          <div className="grid grid-cols-5 gap-2">
            {[
              { id: "stripe", label: "Card" },
              { id: "paypal", label: "PayPal" },
              { id: "cashapp", label: "Cash App" },
              { id: "zelle", label: "Zelle" },
              { id: "cash", label: "Cash" }
            ].map((method) => (
              <button
                key={method.id}
                onClick={() => setMethods({ ...methods, preferred_method: method.id })}
                className={`p-2 rounded-lg border text-center text-sm transition-all ${
                  methods.preferred_method === method.id
                    ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                    : 'border-stone-600 text-stone-400 hover:border-stone-500'
                }`}
              >
                {method.label}
              </button>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={savePaymentMethods}
          disabled={saving}
          className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            <><Check className="w-4 h-4 mr-2" /> Save Payment Methods</>
          )}
        </Button>

        <p className="text-xs text-stone-500 text-center">
          Clients will see these payment options when booking with you.
        </p>
      </CardContent>
    </Card>
  );
};

export default PaymentMethodsCard;
